import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";

const PER_PAGE = 8;
const STOPWORDS = new Set([
    "de","da","do","dos","das","com","para","em","a","o","e","na","no","um","uma",
    "sem","kit","linha","serie","série","tipo","modelo","novo","nova","pack",
]);

/** Assinatura por tipo de produto — 2 primeiras palavras significativas
 *  do name, após remover a marca. Usada para não repetir "disco de corte"
 *  de 3 marcas diferentes na mesma tela. */
function makeSignature(p) {
    const brand = (p.brand?.name || "").toLowerCase();
    const raw = (p.name || "").toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(brand, " ")
        .replace(/[^a-z0-9 ]+/g, " ");
    const tokens = raw.split(/\s+/).filter((t) => t.length > 2 && !STOPWORDS.has(t));
    if (tokens.length === 0) return (p.category?.name || p.category_id || p.id || "").toLowerCase();
    return tokens.slice(0, 2).join(" ");
}

/** Constrói páginas de PER_PAGE produtos, garantindo que cada página tenha
 *  assinaturas únicas (sem 3× disco de corte na mesma tela). Mantém todos
 *  os produtos do pool na rotação. */
function buildDedupedPages(pool) {
    const remaining = [...pool];
    // shuffle inicial (Fisher–Yates) para variar entre reloads
    for (let i = remaining.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
    }
    const pages = [];
    let page = [];
    let sigs = new Set();
    let iter = 0;
    const HARD_STOP = pool.length * 4; // safety

    while (remaining.length > 0 && iter < HARD_STOP) {
        iter++;
        // Tenta encontrar o próximo item cuja assinatura ainda não está na página atual
        let pickIndex = -1;
        for (let i = 0; i < remaining.length; i++) {
            const s = makeSignature(remaining[i]);
            if (!sigs.has(s)) { pickIndex = i; break; }
        }
        if (pickIndex === -1) {
            // Todos os restantes duplicam a página atual — fecha e reinicia
            if (page.length > 0) { pages.push(page); page = []; sigs = new Set(); }
            // Força pegar o primeiro para não travar
            const p = remaining.shift();
            page.push(p);
            sigs.add(makeSignature(p));
        } else {
            const [p] = remaining.splice(pickIndex, 1);
            page.push(p);
            sigs.add(makeSignature(p));
        }
        if (page.length === PER_PAGE) {
            pages.push(page);
            page = [];
            sigs = new Set();
        }
    }
    if (page.length > 0) pages.push(page);
    return pages;
}

export default function ProductShowcase() {
    const [pool, setPool] = useState([]);
    const [rotationMs, setRotationMs] = useState(6000);
    const [pageIndex, setPageIndex] = useState(0);
    const [fade, setFade] = useState(false);

    useEffect(() => {
        let mounted = true;
        Promise.all([
            api.get("/public/products", { params: { has_image: true, limit: 200 } }),
            api.get("/public/showcase-config").catch(() => ({ data: { rotation_seconds: 6 } })),
        ]).then(([prods, cfg]) => {
            if (!mounted) return;
            const ok = (prods.data || []).filter((p) => p.image_url && p.image_url.startsWith("http"));
            setPool(ok);
            const rs = Number(cfg?.data?.rotation_seconds) || 6;
            setRotationMs(Math.max(3, Math.min(60, rs)) * 1000);
        }).catch(() => setPool([]));
        return () => { mounted = false; };
    }, []);

    // Pré-computa páginas com dedup por assinatura. useMemo evita reprocessar
    // a cada render — só rebuilds se o pool mudar.
    const pages = useMemo(() => buildDedupedPages(pool), [pool]);
    const visible = pages[pageIndex] || [];

    useEffect(() => {
        if (pages.length <= 1) return;
        const tid = setInterval(() => {
            setFade(true);
            setTimeout(() => {
                setPageIndex((i) => (i + 1) % pages.length);
                setFade(false);
            }, 320);
        }, rotationMs);
        return () => clearInterval(tid);
    }, [pages.length, rotationMs]);

    if (pool.length === 0) return null;

    return (
        <section className="bg-[#F4F5F7] border-b border-neutral-200" data-testid="product-showcase">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
                <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-[0.24em] text-brand-blue">Do catálogo</div>
                        <h2 className="mt-2 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">
                            Equipamentos técnicos para <span className="text-brand-yellow">indústria, oficina e profissional</span>.
                        </h2>
                        <p className="mt-3 text-sm text-neutral-600 max-w-2xl">
                            Do chão de fábrica à bancada — catálogo real de marcas oficiais, com specs, comparação e orçamento sem cadastro.
                        </p>
                    </div>
                    <Link
                        to="/produtos"
                        data-testid="showcase-cta-catalogo"
                        className="inline-flex items-center gap-2 px-6 h-12 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-blue-hover whitespace-nowrap"
                    >
                        Ver catálogo <ArrowRight size={16} />
                    </Link>
                </div>

                <div
                    className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 transition-opacity duration-300 ${fade ? "opacity-0" : "opacity-100"}`}
                    data-testid="showcase-grid"
                >
                    {visible.map((p, i) => (
                        <Link
                            key={`${p.id}-${pageIndex}-${i}`}
                            to={`/produto/${p.id}`}
                            data-testid={`showcase-card-${i}`}
                            className="group bg-white border border-neutral-200 hover:border-brand-blue transition-colors block overflow-hidden"
                        >
                            <div className="aspect-square bg-white overflow-hidden flex items-center justify-center p-4">
                                <img
                                    src={p.image_url}
                                    alt={p.name}
                                    loading="lazy"
                                    referrerPolicy="no-referrer"
                                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                    onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                                />
                            </div>
                            <div className="border-t border-neutral-200 px-3 py-3">
                                {p.brand?.name && (
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-brand-blue mb-1 truncate">
                                        {p.brand.name}
                                    </div>
                                )}
                                <div className="text-sm font-heading font-bold text-ink line-clamp-2 leading-snug">
                                    {p.name}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                {pages.length > 1 && (
                    <div className="mt-6 flex items-center gap-2 justify-center" data-testid="showcase-dots">
                        {pages.map((_, i) => (
                            <span
                                key={i}
                                className={`h-1 transition-all duration-300 ${pageIndex === i ? "w-8 bg-brand-blue" : "w-3 bg-neutral-300"}`}
                                aria-hidden="true"
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
